import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../../context/ToastContext';
import { useProject } from '../../../context/ProjectContext';
import { deleteContentImage } from '../../../lib/storage';
import MediaPicker from '../../media/MediaPicker';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../lib/supabase';
import { executeSocial } from '../../agents/documentAi';

const nodeName = 'Social Post';

const SOCIAL_NETWORKS = [
  { id: 'facebook', name: 'Facebook' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'x', name: 'X (Twitter)' }
];

const GenerateContentModal = ({ onClose, onGenerate, loading }) => {
  const [topic, setTopic] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    onGenerate({ topic });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-dark-200 rounded-xl p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Generate Content with Clive</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Topic Description */}
          <div>
            <label htmlFor="topic" className="block text-sm font-medium mb-2">
              Topic Description
            </label>
            <textarea
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full px-4 py-2 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
              placeholder="Describe what you want to write about..."
              rows={4}
              required
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FacebookPreview = ({ content, images }) => {
  return (
    <div className="bg-[#242526] rounded-lg shadow overflow-hidden">
      <div className="p-4 flex items-center space-x-2">
        <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
          P
        </div>
        <div>
          <div className="font-semibold text-[#E4E6EB]">Page Name</div>
          <div className="text-xs text-[#B0B3B8]">Just now</div>
        </div>
      </div>
      <div className="px-4 pb-3">
        <div className="text-[#E4E6EB] whitespace-pre-wrap">{content}</div>
      </div>
      {images.length > 0 && (
        <div className={`grid ${images.length === 1 ? '' : 'grid-cols-2'} gap-1`}>
          {images.map((image, index) => (
            <div key={index} className={`relative ${images.length === 1 ? 'h-[300px]' : 'h-[200px]'}`}>
              <img src={image} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}
      <div className="px-4 py-2 border-t border-[#3E4042]">
        <div className="flex justify-between">
          <button className="flex-1 py-2 text-[#B0B3B8] hover:bg-[#3A3B3C] rounded-lg">Like</button>
          <button className="flex-1 py-2 text-[#B0B3B8] hover:bg-[#3A3B3C] rounded-lg">Comment</button>
          <button className="flex-1 py-2 text-[#B0B3B8] hover:bg-[#3A3B3C] rounded-lg">Share</button>
        </div>
      </div>
    </div>
  );
};

const LinkedInPreview = ({ content, images }) => {
  return (
    <div className="bg-[#1B1F23] rounded-lg shadow overflow-hidden">
      <div className="p-4 flex items-center space-x-2">
        <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
          P
        </div>
        <div>
          <div className="font-semibold text-[#E4E6EB]">Profile Name</div>
          <div className="text-sm text-[#B0B3B8]">Company • Just now</div>
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="text-[#E4E6EB] whitespace-pre-wrap">{content}</div>
      </div>
      {images.length > 0 && (
        <div className="mt-2">
          {images.map((image, index) => (
            <img key={index} src={image} alt={`Content ${index + 1}`} className="w-full" />
          ))}
        </div>
      )}
      <div className="px-4 py-2 border-t border-gray-700">
        <div className="flex gap-4">
          <button className="text-[#B0B3B8] hover:bg-gray-700 px-3 py-1 rounded">Like</button>
          <button className="text-[#B0B3B8] hover:bg-gray-700 px-3 py-1 rounded">Comment</button>
          <button className="text-[#B0B3B8] hover:bg-gray-700 px-3 py-1 rounded">Share</button>
        </div>
      </div>
    </div>
  );
};

const XPreview = ({ content, images }) => {
  return (
    <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
      <div className="p-4">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
            P
          </div>
          <div>
            <div className="font-bold text-white">Profile Name</div>
            <div className="text-gray-500">@handle</div>
          </div>
        </div>
        <div className="text-white whitespace-pre-wrap mb-3">{content}</div>
        {images.length > 0 && (
          <div className={`grid ${images.length === 1 ? '' : 'grid-cols-2'} gap-2 rounded-xl overflow-hidden border border-gray-800`}>
            {images.map((image, index) => (
              <div key={index} className="aspect-video">
                <img src={image} alt={`Content ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-between mt-4 text-gray-500">
          <button className="hover:text-blue-400">Reply</button>
          <button className="hover:text-green-400">Repost</button>
          <button className="hover:text-red-400">Like</button>
          <button className="hover:text-blue-400">Share</button>
        </div>
      </div>
    </div>
  );
};

const SocialPreview = ({ content, images, networkType }) => {
  switch (networkType) {
    case 'facebook':
      return <FacebookPreview content={content} images={images} />;
    case 'linkedin':
      return <LinkedInPreview content={content} images={images} />;
    case 'x':
      return <XPreview content={content} images={images} />;
    default:
      return null;
  }
};

export default function SocialContent({ isEditing = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const { currentProject } = useProject();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [images, setImages] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    node_type: ''
  });

  // Add useEffect to fetch content data
  useEffect(() => {
    const fetchContent = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const { data: contentData, error } = await supabase
          .from('content')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;

        setFormData({
          title: contentData.title || '',
          content: contentData.content || '',
          node_type: contentData.node_type || ''
        });
        
        setImages(contentData.images || []);
      } catch (error) {
        console.error('Error fetching content:', error);
        addToast('Failed to load content', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (showMediaPicker) return;
    setLoading(true);

    try {
      const contentData = {
        project_id: currentProject.id,
        title: formData.title,
        content: formData.content,
        node_type: formData.node_type,
        updated_at: new Date().toISOString(),
        updated_by: user.id,
        images: images
      };

      const { error } = await supabase
        .from('content')
        .update(contentData)
        .eq('id', id);

      if (error) throw error;

      addToast(`${nodeName} saved successfully`, 'success');
      
    } catch (error) {
      console.error('Error saving content:', error);
      addToast('Failed to save content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = async (index) => {
    try {
      const imageToRemove = images[index];
      
      // Create new array without the removed image
      const newImages = images.filter((_, i) => i !== index);
      setImages(newImages);
  
      // If image has a media_id, delete it from storage
      if (imageToRemove.media_id) {
        await deleteContentImage(imageToRemove.media_id);
      }
  
    } catch (error) {
      console.error('Error removing image:', error);
      addToast('Failed to remove image', 'error');
    }
  };

  const handleMediaSelect = async (mediaItem) => {
    try {
      // Add the new image to the images array
      setImages(prev => [...prev, mediaItem.url]);
      
      // Close the media picker
      setShowMediaPicker(false);
    } catch (error) {
      console.error('Error selecting media:', error);
      addToast('Failed to select media', 'error');
    }
  };

  const handleGenerateWithAI = async ({ topic }) => {
    try {
      if (!formData.node_type) {
        addToast('Please select a social network first', 'error');
        return;
      }

      setLoading(true);
      setLoadingMessage('Generating content...');
      setShowGenerateModal(false);

      const result = await executeSocial({
        userPrompt: topic,
        projectId: currentProject.id,
        organizationId: currentProject.organization_id,
        userId: user.id,
        networkType: formData.node_type
      });

      if (result.success) {
        // Update form data with generated content
        setFormData(prev => ({
          ...prev,
          title: result.output.title || prev.title,
          content: result.output.content || prev.content
        }));
        
        addToast('Content generated successfully', 'success');
      } else {
        throw new Error('Failed to generate content');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      addToast(error.message || 'Failed to generate content', 'error');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  return (
    <div className="py-6">
      {loading && loadingMessage && <LoadingOverlay message={loadingMessage} />}
      
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-semibold">
              {isEditing ? `Edit ${nodeName}` : `Create ${nodeName}`}
            </h1>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => setShowGenerateModal(true)}
              className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors"
            >
              Generate with Clive
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isEditing ? 'Save Changes' : 'Save Post'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="bg-dark-200 rounded-xl p-6 border border-divider">
            <h2 className="text-lg font-medium mb-6">Create Social Post</h2>
            <form className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Title */}
                <div className="col-span-1">
                  <label htmlFor="title" className="block text-sm font-medium mb-2">
                    Post Title (Internal Reference)
                  </label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
                    placeholder="Enter title"
                    required
                  />
                </div>

                {/* Network Selector */}
                <div className="col-span-1">
                  <label htmlFor="network" className="block text-sm font-medium mb-2">
                    Social Network
                  </label>
                  <select
                    id="network"
                    value={formData.node_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, node_type: e.target.value }))}
                    className="w-full px-4 py-2 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
                    required
                  >
                    <option value="">Select Network</option>
                    {SOCIAL_NETWORKS.map(network => (
                      <option key={network.id} value={network.id}>
                        {network.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Content Textarea */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Post Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full h-40 px-4 py-2 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary resize-none"
                  placeholder="Write your post content here..."
                />
              </div>

              {/* Images Section */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Images
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={image} 
                        alt={`Upload ${index + 1}`}
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XMarkIcon className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={() => setShowMediaPicker(true)}
                    className="flex items-center justify-center h-40 rounded-lg border-2 border-dashed border-divider hover:border-accent-primary transition-colors"
                  >
                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                  </button>
                </div>

                <MediaPicker
                  isOpen={showMediaPicker}
                  onClose={() => setShowMediaPicker(false)}
                  onSelect={handleMediaSelect}
                />
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div>
            <div className="sticky top-6 space-y-6">
              <div className="bg-dark-200 rounded-xl p-6 border border-divider">
                <h2 className="text-lg font-medium mb-6">Post Preview</h2>
                <SocialPreview 
                  content={formData.content}
                  images={images}
                  networkType={formData.node_type}
                />
                
                {/* Preview Notice */}
                <div className="mt-6 p-4 bg-dark-300 rounded-lg border border-divider/50">
                  <p className="text-sm text-gray-400">
                    This is a preview of how your post might look on {
                      formData.node_type === 'facebook' ? 'Facebook' :
                      formData.node_type === 'linkedin' ? 'LinkedIn' :
                      formData.node_type === 'x' ? 'X (Twitter)' :
                      'social media'
                    }. 
                    The actual appearance may vary slightly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showGenerateModal && (
        <GenerateContentModal
          onClose={() => setShowGenerateModal(false)}
          onGenerate={handleGenerateWithAI}
          loading={loading}
        />
      )}

     
    </div>
  );
} 